// frontend/src/pages/dashboard/BillingPage.js
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Divider,
    Alert,
    CircularProgress,
    Collapse,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../context/AuthContext';
import { reportAPI, invoiceAPI } from '../../services/api';

const BillingPage = () => {
    const { user } = useAuth();
    const [billingData, setBillingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedClient, setExpandedClient] = useState(null);

    // ✅ Payment Modal State
    const [paymentModal, setPaymentModal] = useState({
        open: false,
        invoiceId: null,
        amount: ''
    });

    // ✅ Edit Invoice Modal State (Super Admin)
    const [editModal, setEditModal] = useState({
        open: false,
        invoiceId: null,
        amount: '',
        notes: '',
        dueDate: '',
        currency: 'USD'
    });

    const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

    useEffect(() => {
        fetchBillingData();
    }, []);

    const fetchBillingData = async () => {
        try {
            const res = await reportAPI.getBillingSummary();
            setBillingData(res.data);
        } catch (err) {
            console.error('Failed to load billing:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleClient = (clientId) => {
        setExpandedClient(expandedClient === clientId ? null : clientId);
    };

    const canManageInvoices = () => {
        return user?.hasPermission?.('invoice.edit_amount') ||
            user?.hasPermission?.('invoice.send_email') ||
            user?.isAdmin?.();
    };

    // ✅ Helper: Format billing period display
    const formatBillingPeriod = (start, end) => {
        if (!start || !end) return 'N/A';
        const startDate = new Date(start);
        const endDate = new Date(end);
        return `${startDate.toLocaleString('en-US', { month: 'short', year: 'numeric' })} - ${endDate.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
    };


    // ✅ Handle Mark as Paid
    const handleMarkAsPaid = (invoiceId) => {
        setPaymentModal({ open: true, invoiceId, amount: '' });
    };

    const confirmPayment = async () => {
        if (!paymentModal.amount || parseFloat(paymentModal.amount) <= 0) {
            alert('Enter a valid payment amount');
            return;
        }
        setLoadingInvoiceId(paymentModal.invoiceId);

        try {
            const response = await invoiceAPI.markAsPaid(paymentModal.invoiceId, {
                amount: parseFloat(paymentModal.amount),
            });

            const updatedInvoice = response.data.invoice;

            setBillingData(prev => {
                if (!prev) return prev;
                const updated = { ...prev };
                updated.clients = updated.clients.map(client => {
                    const projects = client.projects.map(project => {
                        const invoices = project.invoices.map(inv =>
                            inv.id === updatedInvoice.id
                                ? {
                                    ...inv,
                                    status: updatedInvoice.status,
                                    paid_at: updatedInvoice.paid_at,
                                    paid_amount: updatedInvoice.paid_amount,
                                    payments: updatedInvoice.payments.map(p => ({
                                        id: p.id,
                                        amount: p.amount,
                                        currency: p.currency,
                                        method: p.method,
                                        paid_at: p.paid_at,
                                        notes: p.notes ?? null,
                                    })),
                                }
                                : inv
                        );

                        let projPaid = 0;
                        let projDue = 0;
                        let projOverdue = 0;
                        invoices.forEach(i => {
                            projPaid += Number(i.paid_amount ?? 0);
                            projDue += (Number(i.amount ?? 0) - Number(i.paid_amount ?? 0));
                            if (i.status !== 'paid' && i.due_date && new Date(i.due_date) < new Date()) {
                                projOverdue += Number(i.amount ?? 0) - Number(i.paid_amount ?? 0);
                            }
                        });

                        return { ...project, invoices, project_total_paid: projPaid, project_total_due: projDue, project_total_overdue: projOverdue };
                    });

                    let clientPaid = 0;
                    let clientDue = 0;
                    let clientOverdue = 0;
                    projects.forEach(p => {
                        p.invoices.forEach(i => {
                            clientPaid += Number(i.paid_amount ?? 0);
                            clientDue += Number(i.amount ?? 0) - Number(i.paid_amount ?? 0);
                            if (i.status !== 'paid' && i.due_date && new Date(i.due_date) < new Date()) {
                                clientOverdue += Number(i.amount ?? 0) - Number(i.paid_amount ?? 0);
                            }
                        });
                    });

                    return { ...client, projects, total_paid: clientPaid, total_due: clientDue, total_overdue: clientOverdue };
                });
                return updated;
            });

            setPaymentModal({ open: false, invoiceId: null, amount: '' });
        } catch (err) {
            alert('Failed: ' + (err.response?.data?.error || 'Unknown error'));
        } finally {
            setLoadingInvoiceId(null);
        }
    };

    // ✅ Handle Send Invoice Email (Super Admin)
    const handleSendInvoiceEmail = async (invoiceId) => {
        if (!window.confirm('Send this invoice to the client via email?')) return;

        try {
            await invoiceAPI.sendEmail(invoiceId);
            alert('✅ Invoice email sent successfully!');
        } catch (err) {
            alert('Failed: ' + (err.response?.data?.error || 'Unknown error'));
        }
    };

    // ✅ Handle Edit Invoice (Super Admin)
    const handleEditInvoice = (invoice) => {
        setEditModal({
            open: true,
            invoiceId: invoice.id,
            amount: invoice.amount,
            notes: invoice.notes || '',
            dueDate: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
            currency: invoice.currency || 'USD'
        });
    };

    // ✅ Confirm Edit Invoice
    const confirmEditInvoice = async () => {
        if (!editModal.amount || parseFloat(editModal.amount) <= 0) {
            alert('Enter a valid amount');
            return;
        }

        setLoadingInvoiceId(editModal.invoiceId);

        try {
            await invoiceAPI.update(editModal.invoiceId, {
                amount: parseFloat(editModal.amount),
                notes: editModal.notes,
                due_date: editModal.dueDate
            });

            // Refresh billing data
            await fetchBillingData();
            setEditModal({ open: false, invoiceId: null, amount: '', notes: '', dueDate: '', currency: 'USD' });
            alert('✅ Invoice updated successfully!');
        } catch (err) {
            alert('Failed: ' + (err.response?.data?.error || 'Unknown error'));
        } finally {
            setLoadingInvoiceId(null);
        }
    };

    // ✅ Helper: Format currency
    const fmt = (amt, cur = 'USD') => `${cur} ${Number(amt || 0).toFixed(2)}`;
    // ✅ Helper: Profit color
    const profitColor = (p) => p > 0 ? 'success.main' : p < 0 ? 'error.main' : 'text.secondary';

    // Loading State
    if (loading) {
        return (
            <DashboardLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    // No Data State
    if (!billingData || !billingData.clients || billingData.clients.length === 0) {
        return (
            <DashboardLayout>
                <Box sx={{ p: 3 }}>
                    <Alert severity="info">No billing data available.</Alert>
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Client Billing Summary
                </Typography>

                {billingData.clients.map(client => (
                    <Box key={client.id} sx={{ mb: 3 }}>
                        <Paper sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => toggleClient(client.id)}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">{client.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{client.email}</Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" color="primary" fontWeight="bold">
                                        {client.currency} {Number(client.total_due ?? 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color={client.total_overdue > 0 ? 'error' : 'text.secondary'}>
                                        {client.total_overdue > 0 ? `Overdue: ${client.currency} ${Number(client.total_overdue).toFixed(2)}` : 'No overdue'}
                                    </Typography>
                                    <Typography variant="body2" color="success.main">
                                        Paid: {client.currency} {Number(client.total_paid ?? 0).toFixed(2)}
                                    </Typography>
                                </Box>

                                <IconButton size="small">
                                    <ExpandMoreIcon sx={{ transform: expandedClient === client.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />
                                </IconButton>
                            </Box>
                        </Paper>

                        <Collapse in={expandedClient === client.id}>
                            <Box sx={{ mt: 2, p: 2 }}>
                                {client.projects.map(project => (
                                    <Box key={project.id} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">{project.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {project.billing_model} | {project.currency}
                                            {project.hourly_rate ? ` (${project.hourly_rate}/hr)` : ''}
                                            {project.fixed_price ? ` Fixed: ${project.fixed_price}` : ''}
                                        </Typography>

                                        {/* AUTO BILL CALCULATION */}
                                        {project.billing_model === "hourly" && (
                                            <Typography variant="body2" sx={{ mt: 0.5, mb: 1 }}>
                                                Estimated: <strong>{project.estimated_hours || 0}</strong> hrs × {project.currency} <strong>{project.hourly_rate || 0}</strong>/hr =
                                                <strong style={{ color: "#1976d2", marginLeft: 4 }}>
                                                    {project.currency} {((project.estimated_hours || 0) * (project.hourly_rate || 0)).toFixed(2)}
                                                </strong>
                                            </Typography>
                                        )}

                                        {project.billing_model === "fixed" && (
                                            <Typography variant="body2" sx={{ mt: 0.5, mb: 1 }}>
                                                Fixed Price:
                                                <strong style={{ color: "#1976d2", marginLeft: 4 }}>
                                                    {project.currency} {Number(project.fixed_price).toFixed(2)}
                                                </strong>
                                            </Typography>
                                        )}

                                        {/* Periodic Billing Info */}
                                        {['monthly', 'quarter', 'half-yearly', 'yearly'].includes(project.billing_model) && (
                                            <Typography variant="body2" sx={{ mt: 0.5, mb: 1, color: 'primary.main' }}>
                                                🔄 Recurring: {project.billing_model} | Amount: {project.currency} {Number(project.fixed_price).toFixed(2)} per period
                                            </Typography>
                                        )}

                                        {/* Profit/Loss Card */}
                                        <Box sx={{ mt: 1, p: 1, backgroundColor: '#f0f7ff', borderRadius: 1, border: '1px solid #bbdefb' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}><Typography color="text.secondary">💰 Cost:</Typography><Typography fontWeight="bold">{fmt(project.project_total_cost, project.currency)}</Typography></Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}><Typography color="text.secondary">📊 Billing:</Typography><Typography fontWeight="bold">{fmt(project.project_total_billing, project.currency)}</Typography></Box>
                                            <Divider sx={{ my: 0.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                                                <Typography fontWeight="bold">{(project.project_profit_loss || 0) >= 0 ? '✅ Profit:' : '❌ Loss:'}</Typography>
                                                <Typography fontWeight="bold" color={profitColor(project.project_profit_loss)}>{fmt(project.project_profit_loss, project.currency)} {project.project_profit_margin !== 0 && <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>({project.project_profit_margin >= 0 ? '+' : ''}{project.project_profit_margin}%)</Typography>}</Typography>
                                            </Box>
                                        </Box>

                                        <TableContainer sx={{ mt: 1 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Invoice</TableCell>
                                                        <TableCell>Billing Period</TableCell>
                                                        <TableCell>Amount</TableCell>
                                                        <TableCell>Due Date</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Paid</TableCell>
                                                        <TableCell>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {project.invoices.map(inv => (
                                                        <React.Fragment key={inv.id}>
                                                            <TableRow>
                                                                <TableCell>
                                                                    #{inv.number ?? inv.id}
                                                                    {inv.is_recurring && (
                                                                        <Chip
                                                                            size="small"
                                                                            label="Recurring"
                                                                            color="info"
                                                                            sx={{ ml: 1, fontSize: '0.65rem', height: 20 }}
                                                                        />
                                                                    )}
                                                                </TableCell>

                                                                {/* ✅ Billing Period Column */}
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight={inv.billing_period_start ? 500 : 400}>
                                                                        {formatBillingPeriod(inv.billing_period_start, inv.billing_period_end)}
                                                                    </Typography>
                                                                    {inv.previous_due_amount > 0 && (
                                                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                                            + Prev Due: {inv.currency} {Number(inv.previous_due_amount).toFixed(2)}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>

                                                                <TableCell>
                                                                    <Typography fontWeight="bold">
                                                                        {inv.currency} {Number(inv.amount ?? 0).toFixed(2)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}
                                                                    {inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid' && (
                                                                        <Chip size="small" label="Overdue" color="error" sx={{ ml: 1, fontSize: '0.65rem', height: 20 }} />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={(inv.status || 'pending').toUpperCase()}
                                                                        color={
                                                                            inv.status === 'paid' ? 'success' :
                                                                                inv.status === 'partial' ? 'info' :
                                                                                    inv.status === 'overdue' ? 'error' : 'warning'
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {Number(inv.paid_amount ?? 0) > 0 ? (
                                                                        <Typography color="success.main" fontWeight="bold">
                                                                            {inv.currency} {Number(inv.paid_amount ?? 0).toFixed(2)}
                                                                        </Typography>
                                                                    ) : (
                                                                        <Typography color="text.disabled">-</Typography>
                                                                    )}
                                                                </TableCell>

                                                                {/* ✅ Actions Column */}
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                                        {/* Record Payment Button */}
                                                                        {inv.status !== 'paid' && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="success"
                                                                                onClick={() => handleMarkAsPaid(inv.id)}
                                                                                disabled={loadingInvoiceId === inv.id}
                                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                                            >
                                                                                {loadingInvoiceId === inv.id ? <CircularProgress size={16} /> : 'Pay'}
                                                                            </Button>
                                                                        )}

                                                                        {/* ✅ Send Email Button - Super Admin Only */}
                                                                        {canManageInvoices() && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color="primary"
                                                                                onClick={() => handleSendInvoiceEmail(inv.id)}
                                                                                disabled={loadingInvoiceId === inv.id}
                                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                                            >
                                                                                📧
                                                                            </Button>
                                                                        )}

                                                                        {/* ✅ Edit Button - Super Admin Only */}
                                                                        {canManageInvoices() && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color="warning"
                                                                                onClick={() => handleEditInvoice(inv)}
                                                                                disabled={loadingInvoiceId === inv.id}
                                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                                            >
                                                                                ✏️
                                                                            </Button>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>

                                                            {/* ✅ Payment History Section */}
                                                            {inv.payments && inv.payments.length > 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={7} sx={{ backgroundColor: '#fafafa', py: 1 }}>
                                                                        <Typography fontWeight="bold" sx={{ mb: 1, fontSize: '0.9rem' }}>
                                                                            💳 Payment History
                                                                        </Typography>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow sx={{ '& th': { fontSize: '0.8rem', py: 0.5 } }}>
                                                                                    <TableCell>Date</TableCell>
                                                                                    <TableCell>Amount</TableCell>
                                                                                    <TableCell>Method</TableCell>
                                                                                    <TableCell>Notes</TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {inv.payments.map(p => (
                                                                                    <TableRow key={p.id} sx={{ '& td': { fontSize: '0.85rem', py: 0.5 } }}>
                                                                                        <TableCell>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</TableCell>
                                                                                        <TableCell>
                                                                                            <Typography color="success.main" fontWeight="bold">
                                                                                                {p.currency} {Number(p.amount).toFixed(2)}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>{p.method || 'manual'}</TableCell>
                                                                                        <TableCell>{p.notes || '-'}</TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </React.Fragment>
                                                    ))}

                                                    {/* No Invoices Message */}
                                                    {project.invoices.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No invoices generated yet
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                ))}

                <Divider sx={{ my: 3 }} />

                {/* ✅ Payment Modal */}
                <Dialog open={paymentModal.open} onClose={() => setPaymentModal({ open: false, invoiceId: null, amount: '' })}>
                    <DialogTitle>Record Payment</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            fullWidth
                            label="Payment Amount"
                            type="number"
                            value={paymentModal.amount}
                            onChange={(e) => setPaymentModal(prev => ({ ...prev, amount: e.target.value }))}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {billingData?.clients?.[0]?.currency ?? 'USD'}
                                    </InputAdornment>
                                )
                            }}
                            autoFocus
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Enter the amount received from the client
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPaymentModal({ open: false, invoiceId: null, amount: '' })}>Cancel</Button>
                        <Button variant="contained" color="success" onClick={confirmPayment}>Record Payment</Button>
                    </DialogActions>
                </Dialog>

                {/* ✅ Edit Invoice Modal - Super Admin Only */}
                <Dialog
                    open={editModal?.open}
                    onClose={() => setEditModal({ open: false, invoiceId: null, amount: '', notes: '', dueDate: '', currency: 'USD' })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Edit Invoice #{editModal?.invoiceId}</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <TextField
                            margin="dense"
                            fullWidth
                            label="Amount"
                            type="number"
                            value={editModal?.amount || ''}
                            onChange={(e) => setEditModal(prev => ({ ...prev, amount: e.target.value }))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">{editModal?.currency}</InputAdornment>
                            }}
                            disabled={loadingInvoiceId === editModal?.invoiceId}
                        />
                        <TextField
                            margin="dense"
                            fullWidth
                            label="Due Date"
                            type="date"
                            value={editModal?.dueDate || ''}
                            onChange={(e) => setEditModal(prev => ({ ...prev, dueDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            disabled={loadingInvoiceId === editModal?.invoiceId}
                        />
                        <TextField
                            margin="dense"
                            fullWidth
                            label="Notes"
                            multiline
                            rows={3}
                            value={editModal?.notes || ''}
                            onChange={(e) => setEditModal(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Add internal notes about this invoice..."
                            disabled={loadingInvoiceId === editModal?.invoiceId}
                        />
                        <Alert severity="warning" sx={{ mt: 2, fontSize: '0.85rem' }}>
                            ⚠️ Only Super Admin can edit invoice amounts. Changes will be logged.
                        </Alert>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setEditModal({ open: false, invoiceId: null, amount: '', notes: '', dueDate: '', currency: 'USD' })}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={confirmEditInvoice}
                            disabled={loadingInvoiceId === editModal?.invoiceId || !editModal?.amount}
                        >
                            {loadingInvoiceId === editModal?.invoiceId ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </DashboardLayout>
    );
};

export default BillingPage;